import { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MessageSquare,
  X,
  Send,
  Loader2,
  Bot,
  User,
  Trash2,
  History,
  ChevronDown,
  Sparkles,
  HelpCircle,
  FileText,
  BookOpen,
  AlertTriangle,
} from 'lucide-react';
import { api } from '@/services/api';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

interface Conversation {
  id: string;
  title: string;
  context_type: string;
  last_message_at: string;
}

interface AIAssistantProps {
  contextType?: 'general' | 'waste_profile' | 'compliance' | 'tsdf' | 'manifest' | 'shipping';
  contextData?: Record<string, any>;
}

export default function AIAssistant({ contextType = 'general', contextData = {} }: AIAssistantProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();

  // Fetch conversation history
  const { data: conversations } = useQuery({
    queryKey: ['aiConversations'],
    queryFn: async () => {
      const response = await api.get('/assistant/conversations');
      return response.data.data as Conversation[];
    },
    enabled: isOpen,
  });

  // Fetch suggested questions
  const { data: suggestions } = useQuery({
    queryKey: ['aiSuggestions', contextType],
    queryFn: async () => {
      const response = await api.get(`/assistant/suggestions?context=${contextType}`);
      return response.data.data as string[];
    },
    enabled: isOpen && messages.length === 0,
  });

  // Send message mutation
  const sendMessage = useMutation({
    mutationFn: async (text: string) => {
      const response = await api.post('/assistant/chat', {
        message: text,
        conversationId,
        contextType,
        contextData,
      });
      return response.data.data;
    },
    onSuccess: (data) => {
      setConversationId(data.conversationId);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString(),
        },
      ]);
      queryClient.invalidateQueries({ queryKey: ['aiConversations'] });
    },
  });

  // Delete conversation mutation
  const deleteConversation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/assistant/conversations/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aiConversations'] });
      if (conversationId) {
        setConversationId(null);
        setMessages([]);
      }
    },
  });

  // Load conversation
  const loadConversation = async (id: string) => {
    try {
      const response = await api.get(`/assistant/conversations/${id}`);
      const conv = response.data.data;
      setConversationId(conv.id);
      setMessages(conv.messages || []);
      setShowHistory(false);
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  };

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || sendMessage.isPending) return;

    const userMessage = message.trim();
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: userMessage,
        timestamp: new Date().toISOString(),
      },
    ]);
    setMessage('');
    sendMessage.mutate(userMessage);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setMessage(suggestion);
    inputRef.current?.focus();
  };

  const startNewConversation = () => {
    setConversationId(null);
    setMessages([]);
    setShowHistory(false);
  };

  const getContextIcon = () => {
    switch (contextType) {
      case 'waste_profile':
        return <FileText className="w-4 h-4" />;
      case 'compliance':
        return <AlertTriangle className="w-4 h-4" />;
      case 'tsdf':
        return <BookOpen className="w-4 h-4" />;
      default:
        return <HelpCircle className="w-4 h-4" />;
    }
  };

  const getContextLabel = () => {
    switch (contextType) {
      case 'waste_profile':
        return 'Waste Profile Help';
      case 'compliance':
        return 'Compliance Questions';
      case 'tsdf':
        return 'TSDF Selection';
      case 'manifest':
        return 'Manifest Help';
      case 'shipping':
        return 'DOT Shipping';
      default:
        return 'Regulatory Assistant';
    }
  };

  return (
    <>
      {/* Floating Button — spec §5 Option A: neutral-900 pill with brand sparkle */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-neutral-900 px-4 py-2.5 text-sm font-medium text-white shadow-md transition-colors hover:bg-neutral-800 ${
          isOpen ? 'hidden' : ''
        }`}
        title="Ask HazEasy AI"
        aria-label="Ask HazEasy AI"
      >
        <Sparkles className="w-4 h-4 text-brand-400" />
        <span>Ask HazEasy AI</span>
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-50 w-full sm:w-[420px] h-[100dvh] sm:h-[600px] bg-ai-dark sm:rounded-2xl shadow-2xl border border-ai-medium flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-ai-from to-ai-to p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold">AI Regulatory Assistant</h3>
                <div className="flex items-center gap-1 text-ai-text text-xs">
                  {getContextIcon()}
                  <span>{getContextLabel()}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors min-h-[44px] min-w-[44px]"
                title="Conversation History"
                aria-label="Toggle conversation history"
              >
                <History className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors min-h-[44px] min-w-[44px]"
                aria-label="Close AI Assistant"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* History Panel */}
          {showHistory && (
            <div className="absolute top-16 left-0 right-0 bottom-0 bg-ai-dark z-10 flex flex-col">
              <div className="p-4 border-b border-ai-light flex items-center justify-between">
                <h4 className="text-white font-medium">Conversation History</h4>
                <button
                  onClick={startNewConversation}
                  className="text-sm text-ai-accent hover:text-ai-text transition-colors"
                >
                  + New Chat
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {conversations && conversations.length > 0 ? (
                  conversations.map((conv) => (
                    <div
                      key={conv.id}
                      className={`p-3 rounded-lg mb-2 cursor-pointer transition-colors ${
                        conv.id === conversationId
                          ? 'bg-ai-from/20 border border-ai-accent'
                          : 'bg-ai-medium hover:bg-ai-light'
                      }`}
                      onClick={() => loadConversation(conv.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{conv.title}</p>
                          <p className="text-secondary-400 text-xs mt-1">
                            {new Date(conv.last_message_at).toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteConversation.mutate(conv.id);
                          }}
                          className="p-1 text-secondary-500 hover:text-danger transition-colors min-h-[32px] min-w-[32px]"
                          aria-label="Delete conversation"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-secondary-400 text-center py-8">No conversations yet</p>
                )}
              </div>
              <button
                onClick={() => setShowHistory(false)}
                className="p-3 border-t border-ai-light text-secondary-400 hover:text-white flex items-center justify-center gap-2 min-h-[52px]"
              >
                <ChevronDown className="w-4 h-4" />
                Back to Chat
              </button>
            </div>
          )}

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-4">
                <div className="w-16 h-16 bg-ai-from/20 rounded-full flex items-center justify-center mb-4">
                  <Sparkles className="w-8 h-8 text-ai-accent" />
                </div>
                <h4 className="text-white font-medium mb-2">How can I help you today?</h4>
                <p className="text-secondary-400 text-sm mb-6">
                  Ask me about RCRA regulations, waste classification, DOT shipping, or compliance requirements.
                </p>

                {/* Suggested Questions */}
                {suggestions && suggestions.length > 0 && (
                  <div className="w-full space-y-2">
                    <p className="text-xs text-secondary-500 uppercase tracking-wider mb-2">Suggested Questions</p>
                    {suggestions.slice(0, 4).map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="w-full p-3 bg-ai-medium hover:bg-ai-light rounded-lg text-left text-sm text-secondary-300 hover:text-white transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        msg.role === 'user' ? 'bg-gradient-to-r from-ai-from to-ai-to' : 'bg-ai-medium'
                      }`}
                    >
                      {msg.role === 'user' ? (
                        <User className="w-4 h-4 text-white" />
                      ) : (
                        <Bot className="w-4 h-4 text-ai-accent" />
                      )}
                    </div>
                    <div
                      className={`max-w-[80%] p-3 rounded-2xl ${
                        msg.role === 'user'
                          ? 'bg-gradient-to-r from-ai-from to-ai-to text-white rounded-br-md'
                          : 'bg-ai-medium text-secondary-100 rounded-bl-md'
                      }`}
                    >
                      <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                    </div>
                  </div>
                ))}

                {/* Loading indicator */}
                {sendMessage.isPending && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-ai-medium flex items-center justify-center">
                      <Bot className="w-4 h-4 text-ai-accent" />
                    </div>
                    <div className="bg-ai-medium p-3 rounded-2xl rounded-bl-md">
                      <div className="flex items-center gap-2 text-secondary-400">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input Area */}
          <form onSubmit={handleSubmit} className="p-4 border-t border-ai-light">
            <div className="flex gap-2">
              <textarea
                ref={inputRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                placeholder="Ask about regulations, waste codes, compliance..."
                className="flex-1 px-4 py-3 bg-ai-medium border border-ai-light rounded-xl text-white placeholder-secondary-400 resize-none focus:outline-none focus:ring-2 focus:ring-ai-accent focus:border-transparent"
                rows={1}
                style={{ minHeight: '48px', maxHeight: '120px' }}
                aria-label="Message input"
              />
              <button
                type="submit"
                disabled={!message.trim() || sendMessage.isPending}
                className="px-4 py-3 bg-gradient-to-r from-ai-from to-ai-to text-white rounded-xl hover:from-ai-accent hover:to-ai-from disabled:opacity-50 disabled:cursor-not-allowed transition-all min-h-[48px] min-w-[48px]"
                aria-label="Send message"
              >
                {sendMessage.isPending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
            <p className="text-xs text-secondary-500 mt-2 text-center">
              AI responses are for informational purposes. Verify with current regulations.
            </p>
          </form>
        </div>
      )}
    </>
  );
}
